import { FC } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import { ProgressLabel, RegistrationHeaderProps, StepTypes } from './types'
import { useTranslation } from 'react-i18next'
import { ProgressArrow } from 'features/registration/assets'
import { routes } from 'utils/routes'
import styles from './RegistrationHeader.module.scss'
import headerStyles from 'components/header/Header.module.scss'

const RegistrationHeader: FC<RegistrationHeaderProps<StepTypes>> = ({
  currentStep,
  disableHeaderButtonStep,
  flow,
  progressLabels,
  handleNextStep,
  translationSpace
}) => {
  // ROUTER
  const router = useRouter()
  const { locale: language } = router

  // i18n
  const { t } = useTranslation(`${translationSpace}`)

  /**
   * Generates the progress step based on the given RegistrationStep, stepNumber & ProgressLabel
   *
   * @param {StepTypes} step
   * @param {number} index
   * @param {ProgressLabel} label
   * @returns
   */
  const generateProgressStep = (step: StepTypes, index: number, label: ProgressLabel) => {
    // Check if step is clickable  - only clickable when current step is further in the process
    const clickable = flow.indexOf(step) <= flow.indexOf(currentStep)

    const stepContent = (
      <>
        <span className={styles.step}>{index + 1}</span>
        <span className={styles.label}>{t(label.i18nKey, label.default)}</span>
      </>
    )

    return (
      <li className={classNames({ [styles['is-active']]: currentStep === step })} key={index}>
        {index !== 0 && (
          <span className={styles.arrow}>
            <ProgressArrow />
          </span>
        )}

        {clickable && (!disableHeaderButtonStep || currentStep !== disableHeaderButtonStep) ? (
          <button type="button" className={classNames(styles['progress-content'], styles.button)} onClick={() => handleNextStep(step)}>
            {stepContent}
          </button>
        ) : (
          <div className={styles['progress-content']}>{stepContent}</div>
        )}
      </li>
    )
  }

  return (
    <header className={headerStyles.header}>
      <div className={classNames('container', headerStyles.container)}>
        <nav className={classNames(headerStyles.menu, styles.menu)}>
          <Link href={routes(language).home}>
            <a className={classNames([headerStyles.logo, headerStyles['is-blue']])}>
              <div className={headerStyles.image}>
                <Image src="/images/components/header/logo.svg" alt="Bolt Energie" width={80} height={70} />
              </div>
            </a>
          </Link>
          <ul className={styles['registration-progress']}>
            {flow.map((step, index) => generateProgressStep(step, index, progressLabels[step]))}
          </ul>
        </nav>
      </div>
    </header>
  )
}

export default RegistrationHeader
